/**
 * Main curriculum search hook
 * Combines debounce + filtering with cascade matching
 */
import { useState, useMemo, useCallback } from 'react'
import { useDebouncedValue } from 'hooks'
import { ILvl4Item } from '../types'
import { ISkillsStandards } from 'pages/NewProject/components/ProjectSkillsStandards/interfaces'
import { filterCurriculumTree } from '../utils'
import {
  MIN_SEARCH_CHARS,
  SEARCH_DEBOUNCE_MS,
  MAX_SEARCH_RESULTS
} from '../constants'
import { SearchOptions, SearchMetadata } from '../types'

interface UseCurriculumSearchResult {
  searchTerm: string
  setSearchTerm: (term: string) => void
  debouncedTerm: string
  results: ILvl4Item[]
  isSearching: boolean
  metadata: SearchMetadata | null
  clearSearch: () => void
}

/**
 * Hook for searching curriculum tree with cascade matching
 */
export const useCurriculumSearch = (
  skillsStandards: ISkillsStandards | null,
  options?: SearchOptions
): UseCurriculumSearchResult => {
  const minChars = options?.minChars ?? MIN_SEARCH_CHARS
  const debounceMs = options?.debounceMs ?? SEARCH_DEBOUNCE_MS
  const maxResults = options?.maxResults ?? MAX_SEARCH_RESULTS

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedTerm] = useDebouncedValue(searchTerm, debounceMs)

  const isSearching = searchTerm !== debouncedTerm && searchTerm.length >= minChars

  const { results, metadata } = useMemo(() => {
    if (!skillsStandards || debouncedTerm.length < minChars) {
      return { results: [] as ILvl4Item[], metadata: null }
    }

    const startTime = performance.now()
    const subjects = skillsStandards.curriculumItems?.subjects || []
    const axes = skillsStandards.curriculumItems?.axes || []

    const filtered = filterCurriculumTree(subjects, axes, debouncedTerm, maxResults)
    const executionTimeMs = performance.now() - startTime

    // Count match levels (simplified - based on whether ancestors were matched)
    const meta: SearchMetadata = {
      totalMatches: filtered.length,
      matchLevels: { lv2: 0, lv3: 0, lv4: filtered.length },
      searchTerm: debouncedTerm,
      executionTimeMs
    }

    return { results: filtered, metadata: meta }
  }, [skillsStandards, debouncedTerm, minChars, maxResults])

  const clearSearch = useCallback(() => {
    setSearchTerm('')
  }, [])

  return {
    searchTerm,
    setSearchTerm,
    debouncedTerm,
    results,
    isSearching,
    metadata,
    clearSearch
  }
}
